/**
 * Browser-side `set_window_level`: view-only, auto-run like a read. The
 * image display controls in the viewport overlay, used the same way:
 *  - `preset`: one of the image's presets — the SAME list the preset
 *    dropdown shows for this image; a preset the dropdown shows greyed out
 *    is refused. Names match case-insensitively; an unknown name returns
 *    the available presets so the model can pick or ask.
 *  - `blendPercent`: the fusion blend slider (0–100 %). The overlay shows
 *    it only for a non-PET second image in Alpha Blending mode, so it is
 *    refused otherwise (PET fusion has its own controls).
 *  - `fusionColors`: the fusion colour selector's Grayscale / Cyan–Magenta
 *    entries, shown only for a non-PET second image.
 *
 * Every requested change is checked BEFORE any is applied, so a refusal
 * never leaves the display half-changed. Ports are injected (React-free).
 */
import { asRecord } from '../core';
import type { SetWindowLevelOutput } from '../../contract';

export interface WindowLevelPreset {
    readonly name: string;
    readonly ww: number;
    readonly wc: number;
    readonly disabled?: boolean;
}

export type FusionColors = 'grayscale' | 'cyan-magenta';

/** Which fusion controls the overlay shows right now. */
export type FusionDisplay =
    | 'single-image'
    | 'pet-fusion'
    | 'alpha-blending'
    | 'checkerboard';

export interface WindowLevelDeps {
    /** The dropdown's presets for the loaded image; undefined = no image. */
    presets(): ReadonlyArray<WindowLevelPreset> | undefined;
    /** The dropdown's apply step for one preset. */
    apply(preset: WindowLevelPreset): void;
    fusionDisplay(): FusionDisplay;
    /** The blend slider's change step (percent of the second image). */
    setBlend(percent: number): void;
    /** The fusion colour selector's change step. */
    setFusionColors(colors: FusionColors): void;
}

const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');

const NO_FUSION =
    'no second image is fused with this one; load a second image first';
const PET_FUSION =
    'the second image is PET, which has its own colour and opacity ' +
    'controls in the overlay; change them there';

export function setWindowLevel(
    input: unknown,
    deps: WindowLevelDeps
): SetWindowLevelOutput {
    const o = asRecord(input) ?? {};
    const { preset: rawPreset, blendPercent, fusionColors } = o;

    if (
        rawPreset === undefined &&
        blendPercent === undefined &&
        fusionColors === undefined
    ) {
        return {
            status: 'unavailable',
            reason: 'give a preset, a blend percentage or fusion colours',
        };
    }
    if (
        rawPreset !== undefined &&
        (typeof rawPreset !== 'string' || !rawPreset.trim())
    ) {
        return { status: 'unavailable', reason: 'invalid preset name' };
    }
    if (
        blendPercent !== undefined &&
        (typeof blendPercent !== 'number' ||
            !Number.isInteger(blendPercent) ||
            blendPercent < 0 ||
            blendPercent > 100)
    ) {
        return {
            status: 'unavailable',
            reason: 'the blend must be a whole percentage, 0–100',
        };
    }
    if (
        fusionColors !== undefined &&
        fusionColors !== 'grayscale' &&
        fusionColors !== 'cyan-magenta'
    ) {
        return {
            status: 'unavailable',
            reason: 'fusion colours must be grayscale or cyan-magenta',
        };
    }

    // 1. Check everything.
    let match: WindowLevelPreset | undefined;
    if (rawPreset !== undefined) {
        const presets = deps.presets();
        if (!presets) {
            return { status: 'unavailable', reason: 'no image is loaded' };
        }
        match = presets.find((p) => norm(p.name) === norm(rawPreset));
        if (!match) {
            return {
                status: 'not-found',
                available: presets
                    .filter((p) => !p.disabled)
                    .map((p) => p.name),
            };
        }
        if (match.disabled) {
            return {
                status: 'unavailable',
                reason: `"${match.name}" is not available for this image (greyed out in the preset list)`,
            };
        }
    }
    if (blendPercent !== undefined || fusionColors !== undefined) {
        const display = deps.fusionDisplay();
        if (display === 'single-image') {
            return { status: 'unavailable', reason: NO_FUSION };
        }
        if (display === 'pet-fusion') {
            return { status: 'unavailable', reason: PET_FUSION };
        }
        if (blendPercent !== undefined && display !== 'alpha-blending') {
            return {
                status: 'unavailable',
                reason:
                    'the fusion is in Checkerboard mode, which has no blend ' +
                    'slider; switch it to Alpha Blending first',
            };
        }
    }

    // 2. Apply, the same steps as the overlay controls.
    if (match) {
        deps.apply(match);
    }
    if (blendPercent !== undefined) {
        deps.setBlend(blendPercent);
    }
    if (fusionColors !== undefined) {
        deps.setFusionColors(fusionColors);
    }
    return {
        status: 'ok',
        ...(match
            ? { preset: match.name, window: match.ww, level: match.wc }
            : {}),
        ...(blendPercent !== undefined ? { blendPercent } : {}),
        ...(fusionColors !== undefined ? { fusionColors } : {}),
    };
}
